import { ShippingBlockReason, ShippingRulesConfig, ShippingZoneConfig, ShippingCountryConfig } from '../types/config';

// Re-exported so existing call sites keep `import { ShippingBlockReason } from '../utils/shipping'`.
export type { ShippingBlockReason };

/**
 * Result of resolving shipping for a given (country, postal_code, cart_total).
 *
 * Mirrors the backend `ShippingResult` typed dict in
 * `app/services/shipping_rules.py` — both implementations must stay in
 * sync. Frontend uses this for UI feedback; backend recalculates server-side
 * before forwarding to Shopify/PrestaShop.
 */
export interface ShippingResult {
  supported: boolean;
  country_code: string;
  country_name?: string | null;
  zone_id?: string | null;
  zone_name?: string | null;
  shipping_price: number;
  free_shipping_threshold?: number | null;
  is_free: boolean;
  block_reason?: ShippingBlockReason;
}

/** Returns true if any pattern matches the postal code. Bad regexes are
 *  silently skipped so a single broken pattern doesn't bring the resolver
 *  down — the admin validator catches them at save time. */
export function matchPostalPattern(patterns: string[], postalCode: string): boolean {
  for (const raw of patterns || []) {
    if (typeof raw !== 'string' || raw.length === 0) continue;
    try {
      const re = new RegExp(raw);
      if (re.test(postalCode)) return true;
    } catch {
      // Invalid pattern — skip
    }
  }
  return false;
}

/** First-match-wins zone resolution. Zones must be ordered specific-to-broad
 *  in the config (e.g. Canarias/Baleares before Peninsula). */
export function resolveShippingZone(country: ShippingCountryConfig, postalCode: string): ShippingZoneConfig | null {
  if (!postalCode) return null;
  for (const zone of country.zones || []) {
    const patterns = zone.postal_code_patterns || [];
    if (patterns.length > 0 && matchPostalPattern(patterns, postalCode)) {
      return zone;
    }
  }
  return null;
}

function findCountry(rules: ShippingRulesConfig, code: string): ShippingCountryConfig | null {
  const cc = (code || '').trim().toUpperCase();
  for (const c of rules.countries || []) {
    if ((c.code || '').toUpperCase() === cc) return c;
  }
  return null;
}

/** Pure function — pricing logic identical to the backend resolver. */
export function calculateShippingPrice(
  countryCode: string,
  postalCode: string,
  cartTotal: number,
  rules: ShippingRulesConfig,
): ShippingResult {
  const cc = (countryCode || '').trim().toUpperCase();
  const pc = (postalCode || '').trim();

  // Globally blocked postal codes win.
  if (pc && matchPostalPattern(rules.blocked_postal_code_patterns || [], pc)) {
    return {
      supported: false,
      country_code: cc,
      shipping_price: 0,
      is_free: false,
      block_reason: 'postal_code_blocked',
    };
  }

  const country = findCountry(rules, cc);
  if (!country) {
    return {
      supported: false,
      country_code: cc,
      shipping_price: 0,
      is_free: false,
      block_reason: 'country_unknown',
    };
  }
  if (!country.enabled) {
    return {
      supported: false,
      country_code: cc,
      country_name: country.name,
      shipping_price: 0,
      is_free: false,
      block_reason: 'country_disabled',
    };
  }

  const zone = pc ? resolveShippingZone(country, pc) : null;
  if (zone && !zone.enabled) {
    return {
      supported: false,
      country_code: cc,
      country_name: country.name,
      zone_id: zone.id,
      zone_name: zone.name,
      shipping_price: 0,
      is_free: false,
      block_reason: 'zone_disabled',
    };
  }

  const basePrice = zone ? zone.shipping_price : country.base_shipping_price;
  const threshold = zone ? zone.free_shipping_threshold : country.free_shipping_threshold;
  const isFree = threshold !== null && threshold !== undefined && cartTotal >= threshold;

  return {
    supported: true,
    country_code: cc,
    country_name: country.name,
    zone_id: zone ? zone.id : null,
    zone_name: zone ? zone.name : null,
    shipping_price: isFree ? 0 : Number(basePrice) || 0,
    free_shipping_threshold: threshold ?? null,
    is_free: isFree,
  };
}
